import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { PageBreadcrumbComponent } from '../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ConfirmDialogService } from '../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { ChatTeamAdminApiService } from '../../services/chat-team-admin-api.service';
import { ChatTeamDrawerService } from '../../services/chat-team-drawer.service';
import { ChatTeamAdmin } from '../../models/chat-team-admin.model';

/**
 * Equipos que atienden el chat.
 *
 * Existen porque los modos de enrutamiento Equipo específico, Turnos, Menor carga y Por idioma
 * asignan la conversación a un equipo: sin equipos creados esos modos no se pueden configurar.
 */
@Component({
  selector: 'app-chat-teams-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent],
  templateUrl: './chat-teams-list.component.html'
})
export class ChatTeamsListComponent implements OnInit, OnDestroy {
  items: ChatTeamAdmin[] = [];
  filtered: ChatTeamAdmin[] = [];
  loading = false;
  error = '';

  filtroNombre = '';
  filtroSoloActivos = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private api: ChatTeamAdminApiService,
    private drawer: ChatTeamDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargar();
    this.drawer.entitySaved$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.api.getTeams().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.items = [...data].sort((a, b) => a.name.localeCompare(b.name));
        this.aplicarFiltros();
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'No se pudieron cargar los equipos de chat.';
        this.loading = false;
      }
    });
  }

  aplicarFiltros(): void {
    const term = this.filtroNombre.trim().toLowerCase();
    this.filtered = this.items.filter((t) => {
      if (term && !t.name.toLowerCase().includes(term)) return false;
      if (this.filtroSoloActivos && !t.active) return false;
      return true;
    });
  }

  nuevo(): void { this.drawer.open(); }
  editar(item: ChatTeamAdmin): void { this.drawer.open({ id: item.id }); }
  verMiembros(item: ChatTeamAdmin): void { this.router.navigate(['/chatflows/equipos', item.id]); }

  toggleActivo(item: ChatTeamAdmin): void {
    this.api.updateTeam(item.id, {
      name: item.name,
      description: item.description ?? null,
      defaultLanguage: item.defaultLanguage ?? 'es',
      active: !item.active
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.cargar(),
      error: (err) => this.toast.error({
        title: 'Error',
        message: err.error?.message || 'No se pudo cambiar el estado del equipo.'
      })
    });
  }

  async eliminar(item: ChatTeamAdmin): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar equipo de chat',
      message: `Se eliminará "${item.name}" y sus miembros. Si alguna regla de enrutamiento lo usa, no se podrá eliminar.`
    });
    if (!ok) return;
    this.api.deleteTeam(item.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.toast.deleted('Equipo de chat', `Se eliminó "${item.name}" correctamente.`);
        this.cargar();
      },
      error: (err) => this.toast.error({
        title: 'No se pudo eliminar',
        message: err.error?.message || 'No se pudo eliminar el equipo.'
      })
    });
  }

  volverAChatflows(): void { this.router.navigate(['/chatflows']); }
}
